import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { z } from 'zod'
import { InputText } from '@/components/commons/input-text'
import { ButtonBrandLegacy } from '@/components/ui/button-brand-legacy'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { XCloseIcon } from '@/components/ui/icons'
import { Separator } from '@/components/ui/separator'
import { Spinner } from '@/components/ui/spinner'
import { NeighborhoodSection } from '@/features/subgeo/components/neighborhood-section'
import { useCreateSubgeo } from '@/features/subgeo/queries/use-create-subgeo'
import { useSubgeoStore } from '@/features/subgeo/store/subgeo-store'
import { useTranslation } from '@/translations/i18n'

// Schema for the create subgeo form
const CreateSubgeoFormSchema = z.object({
	name: z
		.string()
		.min(1, 'subgeo:name_required')
		.max(100, 'subgeo:name_too_long')
})
type CreateSubgeoForm = z.infer<typeof CreateSubgeoFormSchema>

export const SubgeoCreateForm = () => {
	const { t } = useTranslation()
	const {
		selectedNeighborhoods,
		clearSelectedNeighborhoods,
		neighborhoods,
		toggleNeighborhood
	} = useSubgeoStore()

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors }
	} = useForm({
		resolver: zodResolver(CreateSubgeoFormSchema)
	})

	const selectedNeighborhoodsList = useMemo(() => {
		return neighborhoods.filter((n) => selectedNeighborhoods.has(n.acp_id))
	}, [neighborhoods, selectedNeighborhoods])

	const { mutate: createSubgeo, isPending } = useCreateSubgeo({
		onSuccess: () => {
			toast.success(t('subgeo:create_subgeo_success'))
			clearSelectedNeighborhoods()
			reset()
		}
	})

	const onSubmit = (data: CreateSubgeoForm) => {
		createSubgeo({
			name: data.name,
			neighborhoodIds: Array.from(selectedNeighborhoods)
		})
	}

	const handleCancel = () => {
		clearSelectedNeighborhoods()
		reset()
	}

	return (
		<>
			<div className='w-full px-2.5 pt-2.5'>
				<InputText
					disabled={isPending}
					errorMessage={
						errors.name ? t('subgeo:name_required') : undefined
					}
					inputSize='sm'
					placeholder={t('subgeo:subgeo_name_placeholder')}
					type='text'
					{...register('name')}
				/>
			</div>

			<Separator orientation='horizontal' />

			<form
				className='flex h-full w-full flex-col gap-2'
				onSubmit={handleSubmit(onSubmit)}
			>
				<div className='flex w-full flex-1 flex-col gap-4 overflow-y-auto p-2.5 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar]:w-1'>
					{selectedNeighborhoodsList.length > 0 && (
						<NeighborhoodSection
							chipVariant='success'
							containerGap={1}
							IconComponent={XCloseIcon}
							infoText={t('subgeo:creating_with_neighborhoods', {
								count: selectedNeighborhoodsList.length
							})}
							isActive={true}
							neighborhoods={selectedNeighborhoodsList}
							onIconClick={toggleNeighborhood}
						/>
					)}

					{selectedNeighborhoodsList.length === 0 && (
						<div className='font-medium text-[13px] text-neutral-600'>
							{t('subgeo:click_map_to_add_neighborhoods')}
						</div>
					)}
				</div>

				<div className='flex w-full shrink-0 justify-between border-neutral-200 border-t px-2.5 py-1.5'>
					<ButtonLegacy
						disabled={isPending}
						onClick={handleCancel}
						size='sm'
						type='button'
						variant='tertiary'
					>
						{t('common:cancel')}
					</ButtonLegacy>
					<ButtonBrandLegacy
						disabled={isPending}
						size='sm'
						type='submit'
					>
						{t('subgeo:create_subgeo')}
						{isPending && <Spinner size={16} />}
					</ButtonBrandLegacy>
				</div>
			</form>
		</>
	)
}
